import { useEffect, useRef } from 'react'

/**
 * Generic modal shell — overlay, focus trap, Escape, focus restore.
 *
 * Added ADDITIVELY: ConfirmModal keeps its own implementation and behaviour
 * untouched, because several pages depend on its exact focus order (it focuses
 * the LAST focusable element, which is deliberate for a confirm dialog). This
 * shell focuses the first, which is right for a form.
 */
const Modal = ({ title, subtitle, onClose, children, footer, maxWidth = 'max-w-2xl', labelledBy }) => {
  const dialogRef = useRef(null)
  const previousFocusRef = useRef(null)
  const headingId = labelledBy || 'cb-modal-title'

  // `onClose` is very often an inline arrow at the call site, so it has a new
  // identity on every parent render. Hold it in a ref and keep it OUT of every
  // effect's dependency list — otherwise an unrelated parent re-render (the
  // Queue page's 1 Hz clock, for one) re-runs the effects below.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })

  // Initial focus + focus restore: MOUNT ONLY.
  //
  // This used to depend on [onClose], so every parent re-render re-ran it and
  // called focus() on the first focusable element again. On the Queue page that
  // fired once a second and yanked focus out of whatever the user was using —
  // which is what made the report-picker <select> snap shut the moment it opened.
  useEffect(() => {
    previousFocusRef.current = document.activeElement
    const dialog = dialogRef.current
    if (!dialog) return undefined
    dialog.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )?.focus()
    return () => { previousFocusRef.current?.focus?.() }
  }, [])

  // Escape + Tab trap, in its own effect with no changing dependencies.
  useEffect(() => {
    const focusables = () => dialogRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onCloseRef.current?.(); return }
      if (e.key !== 'Tab') return
      const list = focusables()
      if (!list.length) return
      const first = list[0]
      const last = list[list.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-cb-navy/40 p-4 sm:p-8" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        /*
         * Capped to the viewport, with only the BODY scrolling (QA, 2026-09-03).
         *
         * Measured on the deployed build: the queue/schedule modal rendered
         * **1266px tall at every viewport height** — it did not adapt — putting
         * "Create schedule" at y=1248, i.e. 448px below the fold on a 1000px
         * window and 648px below on a 1440x800 laptop. Nothing inside the dialog
         * scrolled (a walk of its subtree found zero scroll containers); the only
         * thing that scrolled was the OVERLAY, which takes the title bar and the
         * footer with it. So the primary action was off-screen, and the way back to
         * it was a long scroll through a form whose Cancel button had also left.
         *
         * `max-h` matches the overlay's own padding (p-4 → 2rem total, sm:p-8 →
         * 4rem) so the dialog fills the space the overlay actually offers.
         *
         * Safe for every other modal in the app: in a column flex container with
         * AUTO height, `flex-1` distributes free space and there is none, so a
         * dialog shorter than the cap sizes to its content exactly as before. The
         * overlay keeps `overflow-y-auto` as a belt-and-braces fallback for a
         * viewport too short even for the header and footer.
         */
        className={`flex max-h-[calc(100vh-2rem)] w-full flex-col ${maxWidth} overflow-hidden rounded-xl bg-white shadow-2xl sm:max-h-[calc(100vh-4rem)]`}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id={headingId} className="font-display text-lg text-cb-navy">{title}</h2>
            {subtitle && <p className="mt-1.5 text-[11.5px] text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* `min-h-0` is load-bearing: a flex child's default `min-height:auto`
            refuses to shrink below its content, so without it the cap above would
            be defeated and the dialog would grow tall again. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex shrink-0 items-center gap-2.5 border-t border-gray-200 bg-gray-50 px-5 py-3">{footer}</div>
        )}
      </div>
    </div>
  )
}

export default Modal
